/**
 * LabsTab.tsx
 *
 * Patient-detail labs section. Base sub-tabs: All, Documents, Quest, Biomarkers,
 * History, Orders. The Vibrant tab shows only when the Corp-App "Vibrant" module
 * is enabled for the account; NEW Vibrant orders additionally require the
 * opt-in "vibrant ordering" module. Access Labs (its orders sub-tab and its
 * "+ Order" option) shows only while the opt-in "access labs" module is
 * explicitly active. Paid lab integrations (GetLabs, SiPhox, …)
 * appear as extra sub-tabs + "Order" buttons when their module is enabled for the practice.
 */
import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Printer, RefreshCw, Upload, FlaskConical, Package, X } from 'lucide-react';
import { getPatientLabResults, type LabResult } from '@/services/labs.service';
import {
  getEnabledIntegrations,
  type EnabledIntegration,
  type CreateIntegrationOrderResult,
  type IntegrationOrder,
} from '@/services/integrations.service';
import { useIntegrationOrderCharge } from './labs/useIntegrationOrderCharge';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { ACCESS_LABS_MODULE_KEY, accessOrdersQueryKey } from '@/lib/accessLabs';
import { Button } from '@/components/ui/Button';
import { DocumentsSubTab } from './labs/DocumentsSubTab';
import type { LabPreviewTarget } from './labs/LabPdfPreviewPanel';
import { BiomarkersSubTab } from './labs/BiomarkersSubTab';
import { HistorySubTab } from './labs/HistorySubTab';
import { OrdersSubTab } from './labs/OrdersSubTab';
import { QuestSubTab } from './labs/QuestSubTab';
import { VibrantSubTab } from './labs/VibrantSubTab';
import { AccessSubTab } from './labs/AccessSubTab';
import { QuestLabOrderModal } from './labs/QuestLabOrderModal';
import { VibrantLabOrderModal } from './labs/VibrantLabOrderModal';
import { AccessLabOrderModal } from './labs/AccessLabOrderModal';
import { LabUploadModal } from './labs/LabUploadModal';
import { GetLabsSubTab } from './labs/GetLabsSubTab';
import { GetLabsOrderModal } from './labs/GetLabsOrderModal';
import { SiphoxOrderModal } from './labs/SiphoxOrderModal';
import { TruDiagnosticOrderModal } from './labs/TruDiagnosticOrderModal';
import { MescreenOrderModal } from './labs/MescreenOrderModal';
import { GenovaOrderModal } from './labs/GenovaOrderModal';
import { IntegrationOrdersSubTab } from './labs/IntegrationOrdersSubTab';
import { linkedOrderIdFor, type LinkedOrder } from './labs/linkedOrder';

type SubTab = 'all' | 'documents' | 'biomarkers' | 'history' | 'orders' | 'quest' | 'vibrant' | 'access' | 'getlabs' | 'siphox' | 'trudiagnostic' | 'genova' | 'mescreen';

// Order: All (everything), Documents (uploads only), Quest, Vibrant, then any
// enabled lab-integration/vendor tabs (SiPhox, Genova, GetLabs, TruDiagnostic),
// then the analytics tabs and Orders (appended in TRAILING_SUB_TABS).
const BASE_SUB_TABS: Array<{ key: SubTab; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'documents', label: 'Documents' },
  { key: 'quest', label: 'Quest' },
];

const TRAILING_SUB_TABS: Array<{ key: SubTab; label: string }> = [
  { key: 'biomarkers', label: 'Biomarkers' },
  { key: 'history', label: 'History' },
  { key: 'orders', label: 'Orders' },
];

const SIPHOX_FULFILLMENT_LABELS: Record<string, string> = {
  processing: 'Processing',
  kit_shipped: 'Kit shipped',
  kit_delivered: 'Kit delivered',
  collected: 'Sample collected',
  processing_results: 'Processing results',
  completed: 'Completed',
  rejected: 'Sample rejected',
  cancelled: 'Cancelled',
};

const TRUDIAGNOSTIC_FULFILLMENT_LABELS: Record<string, string> = {
  processing: 'Processing',
  kit_shipped: 'Kit shipped',
  kit_delivered: 'Kit delivered',
  collected: 'Sample collected',
  processing_results: 'Processing results',
  completed: 'Results ready',
  cancelled: 'Cancelled',
};

const MESCREEN_FULFILLMENT_LABELS: Record<string, string> = {
  processing: 'Processing',
  kit_shipped: 'Kit shipped',
  kit_delivered: 'Kit delivered',
  completed: 'Results ready',
  error: 'Delivery problem',
  cancelled: 'Cancelled',
};

const GENOVA_FULFILLMENT_LABELS: Record<string, string> = {
  processing: 'Processing',
  action_needed: 'Action needed (signature / activation)',
  kit_shipped: 'Kit shipped',
  kit_delivered: 'Kit delivered',
  processing_results: 'Processing results',
  completed: 'Results ready',
  cancelled: 'Cancelled',
};

interface LabsTabProps {
  patientId: number;
  onPreview: (target: LabPreviewTarget) => void;
  /** A link straight to one order (linkedOrder.ts): opens its details on arrival. */
  linkedOrder?: LinkedOrder | null;
  onLinkedOrderOpened?: () => void;
}

/** The sub-tabs that list one vendor's orders, keyed by that vendor. */
const VENDOR_ORDER_TABS: SubTab[] = ['siphox', 'trudiagnostic', 'genova', 'mescreen'];

export function LabsTab({ patientId, onPreview, linkedOrder, onLinkedOrderOpened }: LabsTabProps) {
  // A link to a lab order lands on that vendor's sub-tab, where the order is.
  const [selectedTab, setSelectedTab] = useState<SubTab>(
    () => VENDOR_ORDER_TABS.find((t) => t === linkedOrder?.vendorKey) ?? 'all',
  );
  const [historyPreselect, setHistoryPreselect] = useState<string | null>(null);
  const [orderPickerOpen, setOrderPickerOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [vibrantOrderOpen, setVibrantOrderOpen] = useState(false);
  const [accessOrderOpen, setAccessOrderOpen] = useState(false);
  const [getLabsOrderOpen, setGetLabsOrderOpen] = useState(false);
  const [siphoxOrderOpen, setSiphoxOrderOpen] = useState(false);
  const [truDiagnosticOrderOpen, setTruDiagnosticOrderOpen] = useState(false);
  const [mescreenOrderOpen, setMescreenOrderOpen] = useState(false);
  const [genovaOrderOpen, setGenovaOrderOpen] = useState(false);
  // When charge_patient order(s) are placed, collect the fee via the existing
  // Forward payment flow — one prefill line per order (Genova multi-test
  // ordering creates several orders in one go), labeled with the test name.
  // Shared with the Scans/Other BodySpec section (useIntegrationOrderCharge).
  const { startCharge, chargeExisting: chargeExistingOrder, cancelExisting, chargeModal } =
    useIntegrationOrderCharge(patientId);
  const cancelExistingOrder = (integration: EnabledIntegration, order: IntegrationOrder) =>
    cancelExisting(integration, order);
  const [uploadOpen, setUploadOpen] = useState(false);
  const queryClient = useQueryClient();

  // Paid integrations available for this practice.
  const { data: integrations = [] } = useQuery({
    queryKey: ['integrations', 'enabled'],
    queryFn: getEnabledIntegrations,
    staleTime: 5 * 60_000,
  });
  const getLabs = integrations.find((i) => i.key === 'getlabs');
  const siphox = integrations.find((i) => i.key === 'siphox');
  // BodySpec is a scan, not a lab: it lives on Scans/Other (BodySpecScansSection, SCRUM-2217).
  const truDiagnostic = integrations.find((i) => i.key === 'trudiagnostic');
  const mescreen = integrations.find((i) => i.key === 'mescreen');
  const genova = integrations.find((i) => i.key === 'genova');

  // Corp-App "Vibrant" module gates the Vibrant results sub-tab. Fail-open:
  // shown while modules load / on error, hidden only when positively
  // disabled. Vibrant results still surface under the All tab regardless.
  const { enabled: vibrantEnabled } = useModuleAccess('vibrant');
  // ORDERING is a separate opt-in module (fail-closed): the "+ Order" picker
  // offers Vibrant only when "vibrant ordering" is explicitly active. Hidden
  // while loading — late-appear is the accepted failure mode. Disabling
  // ordering stops NEW orders; it never hides past results (which stay on the
  // fail-open 'vibrant' key above).
  const vibrantOrderingEnabled = useModuleAccess('vibrant ordering').explicit === true;
  // Access Labs is one opt-in module (fail-closed, as Vibrant ordering): its
  // orders sub-tab and "+ Order" option show only while it is explicitly
  // active. Access results stay under All whether or not it is on.
  const accessEnabled = useModuleAccess(ACCESS_LABS_MODULE_KEY).explicit === true;
  // Switched off while its sub-tab is open, Access has no tab and no panel:
  // show All instead of an empty one.
  const activeTab: SubTab = selectedTab === 'access' && !accessEnabled ? 'all' : selectedTab;

  const subTabs: Array<{ key: SubTab; label: string }> = [
    ...BASE_SUB_TABS,
    ...(vibrantEnabled ? [{ key: 'vibrant' as SubTab, label: 'Vibrant' }] : []),
    ...(accessEnabled ? [{ key: 'access' as SubTab, label: 'Access' }] : []),
    ...(siphox ? [{ key: 'siphox' as SubTab, label: 'SiPhox' }] : []),
    ...(genova ? [{ key: 'genova' as SubTab, label: 'Genova' }] : []),
    ...(getLabs ? [{ key: 'getlabs' as SubTab, label: 'GetLabs' }] : []),
    ...(truDiagnostic ? [{ key: 'trudiagnostic' as SubTab, label: 'TruDiagnostic' }] : []),
    ...(mescreen ? [{ key: 'mescreen' as SubTab, label: 'mescreen' }] : []),
    ...TRAILING_SUB_TABS,
  ];

  const { data: resultsPage, isLoading } = useQuery({
    queryKey: ['patient-lab-results', patientId],
    queryFn: () => getPatientLabResults(patientId, { perPage: -1 }),
    staleTime: 60_000,
  });

  const results: LabResult[] = resultsPage?.data ?? [];

  function handleRefresh() {
    void queryClient.invalidateQueries({ queryKey: ['patient-lab-results', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-lab-documents', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-biomarkers', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-vibrant-orders', patientId] });
    void queryClient.invalidateQueries({ queryKey: accessOrdersQueryKey(patientId) });
    void queryClient.invalidateQueries({ queryKey: ['patient-lab-order-notes', patientId] });
  }

  const handleViewInHistory = useCallback((testName: string) => {
    setHistoryPreselect(testName);
    setSelectedTab('history');
  }, []);

  // Shared post-order handler: switch to the integration's tab, refresh its
  // orders, and collect payment when the order charges the patient.
  function handleOrdered(
    integration: EnabledIntegration,
    tab: SubTab,
    result: CreateIntegrationOrderResult,
    integrationProductId?: number | null,
  ) {
    setGetLabsOrderOpen(false);
    setSiphoxOrderOpen(false);
    setTruDiagnosticOrderOpen(false);
    setMescreenOrderOpen(false);
    setGenovaOrderOpen(false);
    setSelectedTab(tab);
    void queryClient.invalidateQueries({ queryKey: [`patient-${integration.key}-orders`, patientId] });
    if (result.billingMode === 'charge_patient') {
      startCharge(integration, [{ orderId: result.integrationOrderId, amount: result.amount }], integrationProductId);
    }
  }

  /*
   * Actions on an order that is ALREADY on the table (Finish payment / Cancel)
   * come from useIntegrationOrderCharge: an order parked in awaiting_payment
   * gets a way forward and a way out, through the same charge modal.
   */

  // Genova places one order PER selected test — collect payment for all of
  // them in a single Forward charge, one line per test.
  function handleGenovaOrderedMany(
    integration: EnabledIntegration,
    created: Array<{ result: CreateIntegrationOrderResult; testName: string }>,
    integrationProductId?: number | null,
  ) {
    setGenovaOrderOpen(false);
    setSelectedTab('genova');
    void queryClient.invalidateQueries({ queryKey: [`patient-${integration.key}-orders`, patientId] });
    const toCharge = created
      .filter((c) => c.result.billingMode === 'charge_patient')
      .map((c) => ({ orderId: c.result.integrationOrderId, amount: c.result.amount, label: c.testName }));
    startCharge(integration, toCharge, integrationProductId);
  }

  return (
    <div className="space-y-4 py-2">
      {/*
        SCRUM-1425's "large blank gap between the banner and the sub-tab row" is this
        row. The action group already wraps, so at 375px its five buttons stack ~150px
        tall — and the outer row is `items-center` with no `gap`, so the single-line
        "Labs" heading was vertically centred against that column, leaving a tall band
        of empty white beside it. Wrapping puts the title on its own line and the
        buttons directly under it, which closes the gap.
      */}
      <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Labs</h2>
        <div className="flex flex-wrap items-center gap-2 max-md:w-full">
          <Button size="sm" variant="secondary" onClick={handleRefresh}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Refresh
          </Button>
          <Button size="sm" variant="secondary" onClick={() => window.print()}>
            <Printer className="h-3.5 w-3.5 mr-1" />
            Print
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setUploadOpen(true)}>
            <Upload className="h-3.5 w-3.5 mr-1" />
            Upload
          </Button>
          <Button size="sm" onClick={() => setOrderPickerOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Order
          </Button>
        </div>
      </div>

      {/*
        SCRUM-1425. Fully enabled this renders 12 sub-tabs — roughly 900–1000px of
        uppercase `text-xs` labels in a ~343px content box — with no scroller of its
        own. Without one the browser's native focus-scroll had nothing local to move, so
        it scrolled the clipped section shell instead and the whole page slid sideways
        on every tap ("the position keeps changing with every tap").

        Same idiom as DetailSubNav's section strip and the Tasks filter row: the strip
        scrolls, the items refuse to shrink, the labels never wrap.
      */}
      <div className="flex items-center gap-1 border-b border-slate-200 max-md:overflow-x-auto max-md:scrollbar-hide">
        {subTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => { setSelectedTab(tab.key); if (tab.key !== 'history') setHistoryPreselect(null); }}
            className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors max-md:shrink-0 max-md:whitespace-nowrap ${
              activeTab === tab.key
                ? 'border-b-2 border-blue-600 text-blue-700'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'all' && (
          <DocumentsSubTab patientId={patientId} results={results} resultsLoading={isLoading} onPreview={onPreview} />
        )}
        {activeTab === 'documents' && (
          <DocumentsSubTab patientId={patientId} results={results} resultsLoading={isLoading} onPreview={onPreview} uploadsOnly allowShowDeleted />
        )}
        {activeTab === 'biomarkers' && (
          <BiomarkersSubTab patientId={patientId} onViewInHistory={handleViewInHistory} />
        )}
        {activeTab === 'history' && (
          <HistorySubTab patientId={patientId} preSelectedTest={historyPreselect} />
        )}
        {activeTab === 'orders' && (
          <OrdersSubTab patientId={patientId} results={results} loading={isLoading} onPreview={onPreview} />
        )}
        {activeTab === 'quest' && (
          <QuestSubTab results={results} loading={isLoading} onPreview={onPreview} />
        )}
        {activeTab === 'vibrant' && (
          <VibrantSubTab results={results} loading={isLoading} onPreview={onPreview} />
        )}
        {activeTab === 'access' && accessEnabled && (
          <AccessSubTab patientId={patientId} />
        )}
        {activeTab === 'getlabs' && (
          <GetLabsSubTab patientId={patientId} />
        )}
        {activeTab === 'siphox' && (
          <IntegrationOrdersSubTab
            patientId={patientId}
            integrationKey="siphox"
            openOrderId={linkedOrderIdFor(linkedOrder, 'siphox')}
            onOrderOpened={onLinkedOrderOpened}
            hideCancelled
            fulfillmentLabels={SIPHOX_FULFILLMENT_LABELS}
            emptyMessage={'No SiPhox kits yet. Use the Order button above to ship an at-home test.'}
            onFinishPayment={siphox ? (o) => chargeExistingOrder(siphox, o) : undefined}
            onCancel={siphox ? (o) => void cancelExistingOrder(siphox, o) : undefined}
          />
        )}

        {activeTab === 'trudiagnostic' && (
          <IntegrationOrdersSubTab
            patientId={patientId}
            integrationKey="trudiagnostic"
            openOrderId={linkedOrderIdFor(linkedOrder, 'trudiagnostic')}
            onOrderOpened={onLinkedOrderOpened}
            hideCancelled
            fulfillmentLabels={TRUDIAGNOSTIC_FULFILLMENT_LABELS}
            emptyMessage={'No TruDiagnostic tests yet. Use the Order button above to ship one.'}
            onFinishPayment={truDiagnostic ? (o) => chargeExistingOrder(truDiagnostic, o) : undefined}
            onCancel={truDiagnostic ? (o) => void cancelExistingOrder(truDiagnostic, o) : undefined}
          />
        )}

        {activeTab === 'mescreen' && (
          <IntegrationOrdersSubTab
            patientId={patientId}
            integrationKey="mescreen"
            openOrderId={linkedOrderIdFor(linkedOrder, 'mescreen')}
            onOrderOpened={onLinkedOrderOpened}
            hideCancelled
            fulfillmentLabels={MESCREEN_FULFILLMENT_LABELS}
            emptyMessage={'No mescreen tests yet. Use the Order button above to ship a kit.'}
            onFinishPayment={mescreen ? (o) => chargeExistingOrder(mescreen, o) : undefined}
            onCancel={mescreen ? (o) => void cancelExistingOrder(mescreen, o) : undefined}
          />
        )}

        {activeTab === 'genova' && (
          <IntegrationOrdersSubTab
            patientId={patientId}
            integrationKey="genova"
            openOrderId={linkedOrderIdFor(linkedOrder, 'genova')}
            onOrderOpened={onLinkedOrderOpened}
            hideCancelled
            fulfillmentLabels={GENOVA_FULFILLMENT_LABELS}
            emptyMessage={'No Genova tests yet. Use the Order button above to ship one.'}
            onFinishPayment={genova ? (o) => chargeExistingOrder(genova, o) : undefined}
            onCancel={genova ? (o) => void cancelExistingOrder(genova, o) : undefined}
          />
        )}
      </div>

      <LabOrderPicker
        open={orderPickerOpen}
        onClose={() => setOrderPickerOpen(false)}
        vibrantEnabled={vibrantOrderingEnabled}
        accessEnabled={accessEnabled}
        getLabsEnabled={!!getLabs}
        siphoxEnabled={!!siphox}
        truDiagnosticEnabled={!!truDiagnostic}
        mescreenEnabled={!!mescreen}
        genovaEnabled={!!genova}
        onSelect={(vendor) => {
          setOrderPickerOpen(false);
          if (vendor === 'quest') setOrderOpen(true);
          else if (vendor === 'vibrant') setVibrantOrderOpen(true);
          else if (vendor === 'access') setAccessOrderOpen(true);
          else if (vendor === 'getlabs') setGetLabsOrderOpen(true);
          else if (vendor === 'siphox') setSiphoxOrderOpen(true);
          else if (vendor === 'trudiagnostic') setTruDiagnosticOrderOpen(true);
          else if (vendor === 'mescreen') setMescreenOrderOpen(true);
          else if (vendor === 'genova') setGenovaOrderOpen(true);
        }}
      />

      <QuestLabOrderModal
        patientId={patientId}
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        onOrdered={() => {
          setOrderOpen(false);
          setSelectedTab('orders');
          handleRefresh();
        }}
      />

      <VibrantLabOrderModal
        patientId={patientId}
        open={vibrantOrderOpen}
        onClose={() => setVibrantOrderOpen(false)}
        onOrdered={() => {
          setVibrantOrderOpen(false);
          setSelectedTab('orders');
          handleRefresh();
        }}
      />

      <AccessLabOrderModal
        patientId={patientId}
        open={accessOrderOpen}
        onClose={() => setAccessOrderOpen(false)}
        onOrdered={() => {
          setAccessOrderOpen(false);
          setSelectedTab('access');
          handleRefresh();
        }}
      />

      <LabUploadModal
        patientId={patientId}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false);
          handleRefresh();
        }}
      />

      {getLabs && (
        <GetLabsOrderModal
          patientId={patientId}
          integration={getLabs}
          isOpen={getLabsOrderOpen}
          onClose={() => setGetLabsOrderOpen(false)}
          onOrdered={(result) => handleOrdered(getLabs, 'getlabs', result)}
        />
      )}

      {siphox && (
        <SiphoxOrderModal
          patientId={patientId}
          integration={siphox}
          isOpen={siphoxOrderOpen}
          onClose={() => setSiphoxOrderOpen(false)}
          onOrdered={(result, pid) => handleOrdered(siphox, 'siphox', result, pid)}
        />
      )}

      {mescreen && (
        <MescreenOrderModal
          patientId={patientId}
          integration={mescreen}
          isOpen={mescreenOrderOpen}
          onClose={() => setMescreenOrderOpen(false)}
          onOrdered={(result, pid) => handleOrdered(mescreen, 'mescreen', result, pid)}
        />
      )}

      {truDiagnostic && (
        <TruDiagnosticOrderModal
          patientId={patientId}
          integration={truDiagnostic}
          isOpen={truDiagnosticOrderOpen}
          onClose={() => setTruDiagnosticOrderOpen(false)}
          onOrdered={(result, pid) => handleOrdered(truDiagnostic, 'trudiagnostic', result, pid)}
        />
      )}

      {genova && (
        <GenovaOrderModal
          patientId={patientId}
          integration={genova}
          isOpen={genovaOrderOpen}
          onClose={() => setGenovaOrderOpen(false)}
          onOrdered={(created, pid) => handleGenovaOrderedMany(genova, created, pid)}
        />
      )}

      {chargeModal}
    </div>
  );
}

/* ─── Lab vendor picker ─────────────────────────────────────────────────────
 * A single "+ Order" button opens this chooser listing every available lab
 * option. Quest is always shown (direct integration, no module gate).
 * GetLabs, SiPhox etc. appear only when the practice has that module
 * enabled; Vibrant appears only when the opt-in "vibrant ordering" module is
 * explicitly active (vibrantEnabled here is fed from that key, fail-closed).
 * Access Labs appears the same way, only while the "access labs" module is
 * explicitly active (accessEnabled).
 */
type LabVendor = 'quest' | 'vibrant' | 'access' | 'getlabs' | 'siphox' | 'trudiagnostic' | 'genova' | 'mescreen';

interface LabVendorOption {
  key: LabVendor;
  label: string;
  description: string;
  icon: React.ReactNode;
}

export function LabOrderPicker({
  open,
  onClose,
  onSelect,
  vibrantEnabled,
  accessEnabled = false,
  getLabsEnabled,
  siphoxEnabled,
  truDiagnosticEnabled,
  mescreenEnabled = false,
  genovaEnabled,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (vendor: LabVendor) => void;
  vibrantEnabled: boolean;
  accessEnabled?: boolean;
  getLabsEnabled: boolean;
  siphoxEnabled: boolean;
  truDiagnosticEnabled: boolean;
  mescreenEnabled?: boolean;
  genovaEnabled: boolean;
}) {
  if (!open) return null;

  const vendors: LabVendorOption[] = [
    { key: 'quest',   label: 'Quest Diagnostics', description: 'Send a lab order to Quest',          icon: <FlaskConical className="h-5 w-5 text-blue-600" /> },
    ...(vibrantEnabled ? [{ key: 'vibrant' as LabVendor, label: 'Vibrant Wellness', description: 'Send a lab order to Vibrant', icon: <FlaskConical className="h-5 w-5 text-violet-600" /> }] : []),
    ...(accessEnabled ? [{ key: 'access' as LabVendor, label: 'Access Labs', description: 'Send a lab order to Access Labs', icon: <FlaskConical className="h-5 w-5 text-sky-600" /> }] : []),
    ...(getLabsEnabled ? [{ key: 'getlabs' as LabVendor, label: 'GetLabs',          description: 'Schedule an at-home phlebotomy draw', icon: <FlaskConical className="h-5 w-5 text-emerald-600" /> }] : []),
    ...(siphoxEnabled  ? [{ key: 'siphox'  as LabVendor, label: 'SiPhox Health Kit', description: 'Ship an at-home blood-test kit',        icon: <Package       className="h-5 w-5 text-indigo-600" /> }] : []),
    ...(truDiagnosticEnabled ? [{ key: 'trudiagnostic' as LabVendor, label: 'TruDiagnostic', description: 'Ship an at-home epigenetic / biological-age test', icon: <Package className="h-5 w-5 text-teal-600" /> }] : []),
    ...(mescreenEnabled ? [{ key: 'mescreen' as LabVendor, label: 'mescreen', description: 'Ship an at-home mitochondrial-health test kit', icon: <Package className="h-5 w-5 text-amber-600" /> }] : []),
    ...(genovaEnabled ? [{ key: 'genova' as LabVendor, label: 'Genova Diagnostics', description: 'Ship a clinical lab collection kit (GI, hormone, nutritional)', icon: <FlaskConical className="h-5 w-5 text-rose-600" /> }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-lg bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 bg-slate-50">
          <h2 className="text-base font-semibold text-slate-900">New Lab Order</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <p className="text-xs text-slate-500 mb-2">Choose a lab to order from:</p>
          {vendors.map((lab) => (
            <button
              key={lab.key}
              type="button"
              onClick={() => onSelect(lab.key)}
              className="w-full flex items-center gap-3 rounded-md border border-slate-200 px-4 py-3 text-left hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              {lab.icon}
              <div>
                <div className="text-sm font-medium text-slate-800">{lab.label}</div>
                <div className="text-xs text-slate-500">{lab.description}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
